// src/pages/RequestMap.tsx — Customer Request Map (governed Geospatial surface)
//
// Interactive Leaflet map of geolocated Oracle CX customer requests, served live
// from dw_gold via /api/request-map/*. A separate governed explorer from the
// Asset map (different domain/audience/sensitivity); gates on
// geospatial:request_map (admins bypass).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, SpinnerSize, mergeStyleSets, keyframes, MessageBar, MessageBarType, Dropdown, IDropdownOption } from '@fluentui/react';
import { MapContainer, TileLayer, ScaleControl, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { CaretLeft, ArrowsClockwise, ChatCircleDots, X as PhosphorX, Info } from '@phosphor-icons/react';
import { accessService } from '../services/accessService';
import { boroondaraPalette } from '../theme/boroondaraTheme';
import { BASEMAPS, BasemapKey } from '../components/geospatial/constants';

const p = boroondaraPalette;
const VIOLET = '#6D28D9';
// Centre on the Boroondara LGA (the shared DEFAULT_CENTER sits north in Manningham)
const BOROONDARA_CENTER: [number, number] = [-37.82, 145.06];
const BOROONDARA_ZOOM = 13;
const COMPLAINT = '#DC2626';

/* Fix Leaflet default marker icon paths for webpack */
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface ReqFeature {
  type: 'Feature';
  id: string;
  geometry: { type: 'Point'; coordinates: [number, number] };
  properties: { request_id: string; reference: string | null; subject: string | null; status: string | null; service_type: string | null; complaint: boolean };
}
interface FeatureCollection { type: 'FeatureCollection'; features: ReqFeature[]; meta: { truncated?: boolean }; }
interface FilterOptions { services: string[]; statuses: string[]; wards: string[]; }
interface Stats { total: number; complaints: number; open: number; wards: number; }
interface ReqDetail { requestId: string; detail: Record<string, string>; }

const spin = keyframes({ from: { transform: 'rotate(0)' }, to: { transform: 'rotate(360deg)' } });

const styles = mergeStyleSets({
  page: { minHeight: 'calc(100vh - 52px)', background: '#F5F5F5', fontFamily: "'Segoe UI', system-ui, sans-serif" } as any,
  inner: { maxWidth: 1400, margin: '0 auto', padding: 24, '@media (max-width: 768px)': { padding: 12 } } as any,
  backLink: { display: 'inline-flex', alignItems: 'center', gap: 4, color: VIOLET, fontWeight: 600, fontSize: 13, background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginBottom: 12 } as any,
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 } as any,
  headerLeft: { display: 'flex', gap: 12, alignItems: 'center' },
  iconTile: { width: 36, height: 36, borderRadius: 9, background: VIOLET, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 },
  title: { fontSize: 26, fontWeight: 800, color: p.text, letterSpacing: '-0.01em' },
  subtitle: { fontSize: 13, color: '#747474', marginTop: 2 },
  refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, color: p.text, cursor: 'pointer' } as any,
  filterBar: { background: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 8, padding: '12px 18px', marginBottom: 16, display: 'flex', gap: 18, alignItems: 'flex-end', flexWrap: 'wrap' } as any,
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 } as any,
  filterLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#747474' } as any,
  dateInput: { border: '1px solid #D1D5DB', borderRadius: 4, padding: '5px 8px', fontSize: 13, fontFamily: 'inherit', color: p.text } as any,
  clearBtn: { marginLeft: 'auto', background: 'none', border: 'none', color: VIOLET, fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end', padding: '6px 0' } as any,
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 16, '@media (max-width: 860px)': { gridTemplateColumns: 'repeat(2, 1fr)' } } as any,
  kpiCard: { background: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 10, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  kpiLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#747474' } as any,
  kpiValue: { fontSize: 32, fontWeight: 800, color: p.text, fontVariantNumeric: 'tabular-nums', marginTop: 4 } as any,
  kpiSub: { fontSize: 12, color: '#747474', marginTop: 2 },
  mapWrap: { position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1px solid #E1E1E1', minHeight: 620 } as any,
  truncBar: { position: 'absolute', top: 10, left: 10, zIndex: 500, background: 'rgba(255,251,235,0.96)', border: '1px solid #FDE68A', color: '#92400E', fontSize: 12, borderRadius: 6, padding: '6px 10px', maxWidth: 340, display: 'flex', gap: 6, alignItems: 'flex-start' } as any,
  basemapPick: { position: 'absolute', top: 10, right: 10, zIndex: 500, background: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 6, padding: 4 } as any,
  legend: { position: 'absolute', bottom: 12, right: 12, zIndex: 500, background: 'rgba(255,255,255,0.95)', border: '1px solid #E1E1E1', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: '#5B5B5B', display: 'flex', gap: 12 } as any,
  legendDot: { display: 'inline-flex', alignItems: 'center', gap: 4 },
  detail: { position: 'absolute', top: 0, right: 0, bottom: 0, width: 330, background: '#FFFFFF', borderLeft: '1px solid #E1E1E1', zIndex: 600, padding: 18, overflowY: 'auto', boxShadow: '-4px 0 16px rgba(0,0,0,0.08)' } as any,
  detailHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  detailTitle: { fontSize: 15, fontWeight: 700, color: p.text },
  detailSub: { fontSize: 12, color: VIOLET, fontWeight: 600 },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 } as any,
  attrRow: { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', borderBottom: '1px solid #F3F4F6', fontSize: 12 },
  attrKey: { color: '#747474', flexShrink: 0 },
  attrVal: { color: p.text, fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' } as any,
  gate: { padding: 56, textAlign: 'center', color: '#747474', fontSize: 14, lineHeight: '1.6', background: '#FFFFFF', borderRadius: 10, border: '1px solid #EDEDED', maxWidth: 520, margin: '48px auto' } as any,
  gateTitle: { color: p.text, fontWeight: 700, fontSize: 18, marginBottom: 8 },
  gateLink: { color: VIOLET, fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none' } as any,
  spinner: { animationName: spin, animationDuration: '0.9s', animationIterationCount: 'infinite', animationTimingFunction: 'linear', display: 'inline-flex' } as any,
});

/* Clustered request points; complaints highlighted */
function ClusteredRequests({ features, onClick }: { features: ReqFeature[]; onClick: (id: string) => void }) {
  const map = useMap();
  const ref = useRef<any>(null);
  useEffect(() => {
    if (!features.length) return;
    const cluster = (L as any).markerClusterGroup({
      maxClusterRadius: 50,
      iconCreateFunction: (c: any) => L.divIcon({
        html: `<div style="background:${VIOLET};color:#fff;width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;box-shadow:0 2px 6px rgba(0,0,0,.25);border:2px solid #fff">${c.getChildCount()}</div>`,
        className: '', iconSize: L.point(34, 34),
      }),
    });
    features.forEach((f) => {
      const [lng, lat] = f.geometry.coordinates;
      if (typeof lat !== 'number' || typeof lng !== 'number') return;
      const isComplaint = f.properties.complaint;
      const marker = L.circleMarker([lat, lng], {
        radius: isComplaint ? 7 : 6,
        fillColor: isComplaint ? COMPLAINT : VIOLET,
        color: '#fff', weight: 1.5, fillOpacity: 0.9,
      });
      marker.on('click', () => onClick(f.properties.request_id));
      cluster.addLayer(marker);
    });
    ref.current = cluster;
    map.addLayer(cluster);
    return () => { if (ref.current) { map.removeLayer(ref.current); ref.current = null; } };
  }, [map, features, onClick]);
  return null;
}

function BoundsWatcher({ onBbox }: { onBbox: (bbox: string) => void }) {
  const timer = useRef<any>(null);
  const emit = useCallback((m: L.Map) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const b = m.getBounds();
      onBbox(`${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`);
    }, 400);
  }, [onBbox]);
  const map = useMapEvents({ moveend: () => emit(map), zoomend: () => emit(map) });
  useEffect(() => { emit(map); }, [map, emit]);
  return null;
}

/* ── Flies the map to a target bounds whenever it changes (focus-on-filter) ── */
function FitController({ target }: { target: L.LatLngBoundsExpression | null }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.fitBounds(target, { padding: [50, 50], maxZoom: 16, animate: true });
  }, [map, target]);
  return null;
}

export default function RequestMap() {
  const nav = useNavigate();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  const [opts, setOpts] = useState<FilterOptions>({ services: [], statuses: [], wards: [] });
  const [service, setService] = useState<string[]>([]);
  const [status, setStatus] = useState<string[]>([]);
  const [ward, setWard] = useState<string[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [features, setFeatures] = useState<ReqFeature[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [featLoading, setFeatLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReqDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [basemap, setBasemap] = useState<BasemapKey>('Street');
  const [bbox, setBbox] = useState('');
  const [fitTarget, setFitTarget] = useState<L.LatLngBoundsExpression | null>(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState('');

  // Filters serialise to query params
  const filterParams = useCallback(() => {
    const pr: Record<string, string> = {};
    if (service.length) pr.service = service.join(',');
    if (status.length) pr.status = status.join(',');
    if (ward.length) pr.ward = ward.join(',');
    if (from) pr.from = from;
    if (to) pr.to = to;
    return pr;
  }, [service, status, ward, from, to]);

  // ── Access gate ──
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedGeospatial?: string[] }>('/me');
        if (!alive) return;
        const d = res.data;
        setHasAccess(d.role === 'admin' ? true : Array.isArray(d.allowedGeospatial) && d.allowedGeospatial.includes('request_map'));
      } catch { if (alive) setHasAccess(false); }
    })();
    return () => { alive = false; };
  }, []);

  // ── Filter options once ──
  useEffect(() => {
    if (!hasAccess) return;
    (async () => {
      try {
        const res = await accessService.get<FilterOptions & { meta?: { generatedAt: string } }>('/request-map/filter-options');
        setOpts({ services: res.data.services, statuses: res.data.statuses, wards: res.data.wards });
      } catch { /* non-fatal */ }
    })();
  }, [hasAccess]);

  // ── Fetch features (viewport + filters) ──
  const fetchFeatures = useCallback(async (box: string) => {
    setFeatLoading(true);
    try {
      const res = await accessService.get<FeatureCollection>('/request-map/features', { params: { ...filterParams(), ...(box ? { bbox: box } : {}) } });
      setFeatures(res.data.features);
      setTruncated(!!res.data.meta?.truncated);
    } catch {
      setError('Could not load requests from the warehouse.');
    } finally {
      setFeatLoading(false);
    }
  }, [filterParams]);

  // ── Fetch stats (filters, no bbox) ──
  const fetchStats = useCallback(async () => {
    try {
      const res = await accessService.get<Stats & { meta?: { generatedAt: string } }>('/request-map/stats', { params: filterParams() });
      setStats(res.data);
      if (res.data.meta?.generatedAt) setUpdatedAt(new Date(res.data.meta.generatedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }));
    } catch { /* non-fatal */ }
  }, [filterParams]);

  // Refetch on viewport or filter change
  useEffect(() => {
    if (!hasAccess || !bbox) return;
    fetchFeatures(bbox);
  }, [hasAccess, bbox, fetchFeatures]);
  useEffect(() => {
    if (!hasAccess) return;
    fetchStats();
  }, [hasAccess, fetchStats]);

  // ── Focus the map on the filtered result set when filters change ──
  // (skips the initial mount so the map opens centred on Boroondara)
  const focusFilters = useCallback(async () => {
    try {
      const res = await accessService.get<{ bounds: number[] | null }>('/request-map/extent', { params: filterParams() });
      const b = res.data.bounds;
      if (b && b.length === 4) setFitTarget([[b[1], b[0]], [b[3], b[2]]]);
    } catch { /* focus is best-effort */ }
  }, [filterParams]);
  const didFocusMount = useRef(false);
  useEffect(() => {
    if (!hasAccess) return;
    if (!didFocusMount.current) { didFocusMount.current = true; return; }
    focusFilters();
  }, [hasAccess, focusFilters]);

  // ── Detail on selection ──
  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    let alive = true;
    setDetailLoading(true);
    (async () => {
      try {
        const res = await accessService.get<ReqDetail>(`/request-map/feature/${encodeURIComponent(selected)}`);
        if (alive) setDetail(res.data);
      } catch { if (alive) setDetail(null); }
      finally { if (alive) setDetailLoading(false); }
    })();
    return () => { alive = false; };
  }, [selected]);

  const hasActiveFilter = service.length > 0 || status.length > 0 || ward.length > 0 || !!from || !!to;
  const clearFilters = () => { setService([]); setStatus([]); setWard([]); setFrom(''); setTo(''); };

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchStats(), bbox ? fetchFeatures(bbox) : Promise.resolve()]);
    setRefreshing(false);
  };

  if (hasAccess === null) {
    return <div className={styles.page}><div style={{ padding: 80, textAlign: 'center' }}><Spinner size={SpinnerSize.large} label="Checking access…" /></div></div>;
  }
  if (hasAccess === false) {
    return (
      <div className={styles.page}>
        <div className={styles.gate}>
          <div className={styles.gateTitle}>Access Required</div>
          <div>The Customer Request Map draws on governed customer-request data. Request access to view it, or head back to Geospatial.</div>
          <div style={{ marginTop: 16, display: 'flex', gap: 16, justifyContent: 'center' }}>
            <button className={styles.gateLink} onClick={() => nav('/onboard')}>Request access →</button>
            <button className={styles.gateLink} onClick={() => nav('/geospatial')}>Back to Geospatial</button>
          </div>
        </div>
      </div>
    );
  }

  const basemapOptions: IDropdownOption[] = (Object.keys(BASEMAPS) as BasemapKey[]).map((k) => ({ key: k, text: k }));
  const toDD = (arr: string[]): IDropdownOption[] => arr.map((v) => ({ key: v, text: v }));

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <button className={styles.backLink} onClick={() => nav('/geospatial')}><CaretLeft size={13} weight="bold" /> Geospatial</button>

        <div className={styles.headerRow}>
          <div className={styles.headerLeft}>
            <div className={styles.iconTile}><ChatCircleDots size={20} weight="fill" /></div>
            <div>
              <div className={styles.title}>Customer Request Map</div>
              <div className={styles.subtitle}>Source: Oracle CX via dw_gold{updatedAt ? ` · Updated ${updatedAt}` : ''} · geolocated requests only</div>
            </div>
          </div>
          <button className={styles.refreshBtn} onClick={refresh}>
            <span className={refreshing ? styles.spinner : undefined}><ArrowsClockwise size={14} weight="bold" /></span> Refresh
          </button>
        </div>

        {error && (
          <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')} styles={{ root: { borderRadius: 8, marginBottom: 14 } }}>{error}</MessageBar>
        )}

        {/* Filter bar */}
        <div className={styles.filterBar}>
          <div className={styles.filterGroup} style={{ minWidth: 180 }}>
            <span className={styles.filterLabel}>Service</span>
            <Dropdown multiSelect placeholder="All services" selectedKeys={service} options={toDD(opts.services)}
              onChange={(_, o) => o && setService((prev) => o.selected ? [...prev, o.key as string] : prev.filter((k) => k !== o.key))}
              styles={{ root: { width: 190 } }} />
          </div>
          <div className={styles.filterGroup} style={{ minWidth: 160 }}>
            <span className={styles.filterLabel}>Status</span>
            <Dropdown multiSelect placeholder="All statuses" selectedKeys={status} options={toDD(opts.statuses)}
              onChange={(_, o) => o && setStatus((prev) => o.selected ? [...prev, o.key as string] : prev.filter((k) => k !== o.key))}
              styles={{ root: { width: 170 } }} />
          </div>
          <div className={styles.filterGroup} style={{ minWidth: 150 }}>
            <span className={styles.filterLabel}>Ward</span>
            <Dropdown multiSelect placeholder="All wards" selectedKeys={ward} options={toDD(opts.wards)}
              onChange={(_, o) => o && setWard((prev) => o.selected ? [...prev, o.key as string] : prev.filter((k) => k !== o.key))}
              styles={{ root: { width: 160 } }} />
          </div>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Created from</span>
            <input type="date" className={styles.dateInput} value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Created to</span>
            <input type="date" className={styles.dateInput} value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          {hasActiveFilter && <button className={styles.clearBtn} onClick={clearFilters}>Clear filters</button>}
        </div>

        {/* KPI row */}
        <div className={styles.kpiRow}>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>In view</div>
            <div className={styles.kpiValue}>{features.length.toLocaleString('en-AU')}</div>
            <div className={styles.kpiSub}>requests shown</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>Matching filters</div>
            <div className={styles.kpiValue}>{stats ? stats.total.toLocaleString('en-AU') : '—'}</div>
            <div className={styles.kpiSub}>geolocated total</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>Open</div>
            <div className={styles.kpiValue}>{stats ? stats.open.toLocaleString('en-AU') : '—'}</div>
            <div className={styles.kpiSub}>not yet closed</div>
          </div>
          <div className={styles.kpiCard} style={{ borderLeft: `3px solid ${COMPLAINT}` }}>
            <div className={styles.kpiLabel}>Complaints</div>
            <div className={styles.kpiValue} style={{ color: COMPLAINT }}>{stats ? stats.complaints.toLocaleString('en-AU') : '—'}</div>
            <div className={styles.kpiSub}>flagged as complaint</div>
          </div>
        </div>

        {/* Map */}
        <div className={styles.mapWrap}>
          {(truncated || featLoading) && (
            <div className={styles.truncBar}>
              {featLoading
                ? <><span className={styles.spinner}><ArrowsClockwise size={13} weight="bold" /></span><span>Loading requests…</span></>
                : <><Info size={14} weight="bold" style={{ flexShrink: 0, marginTop: 1 }} /><span>Showing a capped sample. Zoom in or narrow filters to see all in view.</span></>}
            </div>
          )}
          <div className={styles.basemapPick}>
            <Dropdown selectedKey={basemap} options={basemapOptions} onChange={(_, o) => o && setBasemap(o.key as BasemapKey)} styles={{ root: { width: 120 }, title: { fontSize: 12, height: 30, lineHeight: '28px' } }} />
          </div>
          <MapContainer center={BOROONDARA_CENTER} zoom={BOROONDARA_ZOOM} style={{ height: '100%', width: '100%', minHeight: 620 }} scrollWheelZoom>
            <TileLayer url={BASEMAPS[basemap].url} attribution={BASEMAPS[basemap].attribution} />
            <ScaleControl position="bottomleft" />
            <BoundsWatcher onBbox={(b) => setBbox(b)} />
            <FitController target={fitTarget} />
            <ClusteredRequests features={features} onClick={(id) => setSelected(id)} />
          </MapContainer>
          <div className={styles.legend}>
            <span className={styles.legendDot}><span style={{ width: 9, height: 9, borderRadius: '50%', background: VIOLET, display: 'inline-block' }} /> Request</span>
            <span className={styles.legendDot}><span style={{ width: 9, height: 9, borderRadius: '50%', background: COMPLAINT, display: 'inline-block' }} /> Complaint</span>
          </div>

          {selected && (
            <div className={styles.detail}>
              <div className={styles.detailHead}>
                <div>
                  <div className={styles.detailSub}>Customer Request</div>
                  <div className={styles.detailTitle}>{detail?.detail?.Reference || selected}</div>
                </div>
                <button className={styles.closeBtn} onClick={() => setSelected(null)}><PhosphorX size={18} weight="bold" /></button>
              </div>
              {detailLoading && <Spinner size={SpinnerSize.medium} label="Loading detail…" />}
              {!detailLoading && detail && Object.entries(detail.detail).length > 0 && (
                Object.entries(detail.detail).map(([k, v]) => (
                  <div key={k} className={styles.attrRow}>
                    <span className={styles.attrKey}>{k}</span>
                    <span className={styles.attrVal}>{v}</span>
                  </div>
                ))
              )}
              {!detailLoading && (!detail || Object.entries(detail.detail).length === 0) && (
                <div style={{ fontSize: 13, color: '#9CA3AF' }}>No detail available for this request.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
